import {
  bbox,
  booleanPointInPolygon,
  featureCollection,
  intersect,
  isobands,
  point,
  pointGrid,
  toMercator
} from '@turf/turf'
import type { ContinuousStop } from 'api-validations/stitch'
import type { BBox, Feature, FeatureCollection, MultiPolygon, Point, Polygon } from 'geojson'
import type { WeightedValue } from 'utils'

import { fitVariogram, type KrigingSample, krigingPredictor } from './kriging'
import { soilBandColor } from './soil-sample-scale'

/**
 * Cells across the field's width. Fixed as a count rather than a ground
 * distance so the cost of interpolating is the same for a 20 acre field and a
 * 200 acre one.
 */
const GRID_COLUMNS = 64

/**
 * Below this there is no surface to speak of — a variogram fitted to three
 * points describes the three points.
 */
const MIN_KRIGED_SAMPLES = 4

/**
 * Above this the interpolation stops being worth its wait on the main thread.
 * Every grid cell costs a solve against every sample, so the work grows with the
 * square of the sample count; a grid density that is instant at fifty samples
 * is not at two thousand.
 */
const MAX_KRIGED_SAMPLES = 200

const Z_PROPERTY = 'value'

type ZoneProperties = { fillColor: string }

export type SoilSurface = FeatureCollection<Point, { [Z_PROPERTY]: number }>

export type SoilZones = FeatureCollection<Polygon | MultiPolygon, ZoneProperties>

type SamplePoint = {
  geometry: Point
  properties: { values: Record<string, number | undefined> }
}

/**
 * Interpolates one analyte across a field, as a grid of predicted values.
 *
 * Separate from cutting the result into bands because this is the expensive half
 * and it does not depend on the bands: moving the legend's bounds recolors the
 * same surface, and re-solving the kriging system for it would make the slider
 * lurch.
 *
 * Derived at view time and not stored: the surface is a function of the readings
 * and nothing else, so persisting it would only add a thing to invalidate.
 *
 * `null` when there is nothing worth interpolating — too few readings, too many
 * to do it here, or a set of readings a variogram cannot be fitted to.
 */
export const interpolateSoilSurface = ({
  samples,
  measurement,
  boundary
}: {
  samples: readonly SamplePoint[]
  measurement: string
  boundary: Feature<Polygon | MultiPolygon>
}): SoilSurface | null => {
  const measured = toKrigingSamples({ samples, measurement })
  if (measured.length < MIN_KRIGED_SAMPLES || measured.length > MAX_KRIGED_SAMPLES) return null

  const [west, south, east, north] = bbox(boundary)
  const cellSide = (east - west) / GRID_COLUMNS
  if (cellSide <= 0) return null

  const predict = predictorFor(measured)
  if (predict === null) return null

  // One cell past the field on every side, so the bands traced between grid points reach its edges.
  const extent: BBox = [west - cellSide, south - cellSide, east + cellSide, north + cellSide]
  const grid = pointGrid(extent, cellSide, { units: 'degrees' })
  const predicted = grid.features.flatMap((cell) => {
    const [longitude, latitude] = cell.geometry.coordinates
    const [x, y] = toMercator(point([longitude, latitude])).geometry.coordinates
    const value = predict({ x, y })
    return Number.isFinite(value) ? [point([longitude, latitude], { [Z_PROPERTY]: value })] : []
  })

  return predicted.length === 0 ? null : featureCollection(predicted)
}

/**
 * Readings that agree everywhere need no variogram — the surface is the reading.
 *
 * Worth its own branch because a variogram cannot be fitted to them at all: every
 * pair has zero semivariance, so there is no sill and nothing to solve. An
 * analyte a lab measured as the same value across a field is a real, flat result,
 * and zero is one of its ordinary values.
 */
const predictorFor = (
  measured: readonly KrigingSample[]
): ((point: { x: number; y: number }) => number) | null => {
  const [first, ...rest] = measured
  if (first === undefined) return null

  if (rest.every(({ value }) => value === first.value)) return () => first.value

  const variogram = fitVariogram(measured)
  if (variogram === null) return null

  return krigingPredictor({ samples: measured, variogram })
}

/**
 * Cuts an interpolated surface into polygons, colored by the same bands the
 * legend and the sample points use, and clipped to the field.
 */
export const bandSoilSurface = ({
  surface,
  boundary,
  bands
}: {
  surface: SoilSurface
  boundary: Feature<Polygon | MultiPolygon>
  bands: readonly ContinuousStop[]
}): SoilZones | null => {
  if (bands.length === 0) return null

  const { lowest, highest } = surfaceRange(surface)
  if (!Number.isFinite(lowest) || !Number.isFinite(highest)) return null

  const interior = bandEdgesWithin({ bands, lowest, highest })

  // A surface crossing no band edge has no contour to trace, and marching squares
  // returns nothing rather than the whole field. It is still one zone — the field,
  // in the band its values fall in — whether that is a flat reading or one
  // smoothed into a narrow range.
  if (interior.length === 0) {
    const color = soilBandColor({ value: (lowest + highest) / 2, bands })
    return color === undefined
      ? null
      : featureCollection([{ ...boundary, properties: { fillColor: color } }])
  }

  const edges = [lowest, ...interior, highest]

  const zones = surfaceBands({ surface, boundary, edges }).flatMap((band, index) => {
    const lower = edges[index]
    const upper = edges[index + 1]
    if (band === null || lower === undefined || upper === undefined) return []

    const color = soilBandColor({ value: (lower + upper) / 2, bands })
    if (color === undefined) return []

    return [{ ...band, properties: { fillColor: color } }]
  })

  return zones.length === 0 ? null : featureCollection(zones)
}

export const soilSurfaceValues = ({
  surface,
  boundary
}: {
  surface: SoilSurface
  boundary: Feature<Polygon | MultiPolygon>
}): WeightedValue[] =>
  surface.features.flatMap((cell) =>
    booleanPointInPolygon(cell, boundary) ? [{ value: cell.properties[Z_PROPERTY], weight: 1 }] : []
  )

export const zoneSoilSurface = ({
  surface,
  boundary,
  breaks
}: {
  surface: SoilSurface
  boundary: Feature<Polygon | MultiPolygon>
  breaks: readonly number[]
}): Feature<Polygon | MultiPolygon>[][] => {
  const { lowest, highest } = surfaceRange(surface)
  return surfaceBands({ surface, boundary, edges: [lowest, ...breaks, highest] }).map((band) =>
    band === null ? [] : [band]
  )
}

const surfaceRange = (surface: SoilSurface) => {
  const values = surface.features.map((cell) => cell.properties[Z_PROPERTY])
  return { lowest: Math.min(...values), highest: Math.max(...values) }
}

const surfaceBands = ({
  surface,
  boundary,
  edges
}: {
  surface: SoilSurface
  boundary: Feature<Polygon | MultiPolygon>
  edges: number[]
}): (Feature<Polygon | MultiPolygon> | null)[] => {
  const [lowestEdge = 0] = edges
  const floor = lowestEdge - 1 - Math.abs((edges.at(-1) ?? lowestEdge) - lowestEdge)
  return isobands(enclosed({ surface, floor }), edges, { zProperty: Z_PROPERTY }).features.map(
    (band) => clipToBoundary({ band, boundary })
  )
}

// Marching squares only closes a band's outer ring along the grid's border when the band has no
// other rings, so a band covering the whole border around interior holes comes back as just its
// holes. A ring of points below every band keeps every band off the border.
const enclosed = ({ surface, floor }: { surface: SoilSurface; floor: number }): SoilSurface => {
  const distinct = (values: number[]) => [...new Set(values)].toSorted((a, b) => a - b)
  const longitudes = distinct(surface.features.map((cell) => cell.geometry.coordinates[0]))
  const latitudes = distinct(surface.features.map((cell) => cell.geometry.coordinates[1]))
  const beyond = (values: number[]) => {
    const step = values[1] - values[0]
    return { before: values[0] - step, after: values[values.length - 1] + step }
  }
  const { before: west, after: east } = beyond(longitudes)
  const { before: south, after: north } = beyond(latitudes)
  const sentinel = (coordinates: [number, number]) => point(coordinates, { [Z_PROPERTY]: floor })

  return featureCollection([
    ...surface.features,
    ...[west, ...longitudes, east].flatMap((longitude) => [
      sentinel([longitude, south]),
      sentinel([longitude, north])
    ]),
    ...latitudes.flatMap((latitude) => [sentinel([west, latitude]), sentinel([east, latitude])])
  ])
}

const toKrigingSamples = ({
  samples,
  measurement
}: {
  samples: readonly SamplePoint[]
  measurement: string
}): KrigingSample[] =>
  samples.flatMap((sample) => {
    const value = sample.properties.values[measurement]
    if (value === undefined) return []

    const [x, y] = toMercator(point(sample.geometry.coordinates)).geometry.coordinates
    return [{ x, y, value }]
  })

/**
 * The band edges the surface actually crosses.
 *
 * The edges span the readings that were taken, and a smoothed surface covers less
 * ground than they do, so the ones it never reaches would contour nothing.
 */
const bandEdgesWithin = ({
  bands,
  lowest,
  highest
}: {
  bands: readonly ContinuousStop[]
  lowest: number
  highest: number
}): number[] =>
  bands.flatMap(({ max }) => (max !== undefined && max > lowest && max < highest ? [max] : []))

const clipToBoundary = ({
  band,
  boundary
}: {
  band: Feature<Polygon | MultiPolygon>
  boundary: Feature<Polygon | MultiPolygon>
}): Feature<Polygon | MultiPolygon> | null => {
  // An isoband no cell fell into comes back with no rings at all.
  if (band.geometry.coordinates.length === 0) return null

  return intersect(featureCollection([band, boundary]))
}
